import { db } from '@/lib/db'
import { supabase } from '@/lib/supabase'
import { teamToRow, makeTeamCode } from '@/lib/mappers'
import { useAuthStore } from '@/store/authStore'
import type { Team } from '@/types'

export async function upsertTeam(team: Team): Promise<void> {
  const userId = useAuthStore.getState().user?.id
  if (!userId) throw new Error('Not authenticated')

  const withCode: Team = team.teamCode ? team : { ...team, teamCode: makeTeamCode() }
  const { error } = await supabase.from('teams').upsert(teamToRow(withCode, userId))
  if (error) throw new Error(error.message)
  // Strip myRole — it's volatile and recalculated from team_members on every hydration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { myRole: _myRole, ...toCache } = withCode
  await db.teams.put(toCache)
}

export async function deleteTeam(teamId: string): Promise<void> {
  await db.transaction('rw', [
    db.teams, db.seasons, db.seasonRosters, db.games,
    db.lineups, db.lineupSlots, db.statEntries, db.events,
  ], async () => {
    const seasons = await db.seasons.where('teamId').equals(teamId).toArray()
    for (const season of seasons) {
      const games = await db.games.where('seasonId').equals(season.id).toArray()
      for (const game of games) {
        const lineups = await db.lineups.where('gameId').equals(game.id).toArray()
        for (const lineup of lineups) {
          await db.lineupSlots.where('lineupId').equals(lineup.id).delete()
        }
        await db.lineups.where('gameId').equals(game.id).delete()
        await db.statEntries.where('gameId').equals(game.id).delete()
      }
      await db.games.where('seasonId').equals(season.id).delete()
      await db.seasonRosters.where('seasonId').equals(season.id).delete()
      await db.events.where('seasonId').equals(season.id).delete()
    }
    await db.seasons.where('teamId').equals(teamId).delete()
    await db.teams.delete(teamId)
  })
}

/**
 * Permanently deletes a team and ALL its data from both Supabase and local Dexie.
 * Cascade in Supabase handles seasons → games → lineups → stats → events → rosters.
 * Players exclusively on this team are also deleted.
 */
export async function deleteTeamEverywhere(teamId: string): Promise<void> {
  // Identify players only on this team
  const teamSeasons = await db.seasons.where('teamId').equals(teamId).toArray()
  const teamSeasonIds = teamSeasons.map(s => s.id)
  const teamRosters = teamSeasonIds.length
    ? await db.seasonRosters.where('seasonId').anyOf(teamSeasonIds).toArray()
    : []
  const teamPlayerIds = [...new Set(teamRosters.map(r => r.playerId))]

  const allOtherRosters = await db.seasonRosters.toArray()
  const otherSeasonIds = new Set(
    (await db.seasons.toArray()).filter(s => s.teamId !== teamId).map(s => s.id)
  )
  const playersOnOtherTeams = new Set(
    allOtherRosters.filter(r => otherSeasonIds.has(r.seasonId)).map(r => r.playerId)
  )
  const playersToDelete = teamPlayerIds.filter(id => !playersOnOtherTeams.has(id))

  // Delete from Supabase (cascade handles all child data)
  const { error: teamErr } = await supabase.from('teams').delete().eq('id', teamId)
  if (teamErr) throw new Error(`Delete team from cloud: ${teamErr.message}`)

  if (playersToDelete.length > 0) {
    const { error } = await supabase.from('players').delete().in('id', playersToDelete)
    if (error) throw new Error(`Delete players from cloud: ${error.message}`)
  }

  // Mirror in local Dexie cache
  await deleteTeam(teamId)
  if (playersToDelete.length > 0) {
    await db.players.bulkDelete(playersToDelete)
  }
}

/**
 * Deletes all games, stats, lineups, and events for a season.
 * Keeps the season record and roster intact.
 */
export async function deleteSeasonData(seasonId: string): Promise<void> {
  // Supabase: game cascade handles lineups → lineup_slots → stat_entries
  const [gRes, eRes] = await Promise.all([
    supabase.from('games').delete().eq('season_id', seasonId),
    supabase.from('events').delete().eq('season_id', seasonId),
  ])
  if (gRes.error) throw new Error(gRes.error.message)
  if (eRes.error) throw new Error(eRes.error.message)

  // Mirror cascade in Dexie
  await db.transaction('rw', [db.games, db.lineups, db.lineupSlots, db.statEntries, db.events], async () => {
    const games = await db.games.where('seasonId').equals(seasonId).toArray()
    for (const game of games) {
      const lineups = await db.lineups.where('gameId').equals(game.id).toArray()
      for (const lineup of lineups) await db.lineupSlots.where('lineupId').equals(lineup.id).delete()
      await db.lineups.where('gameId').equals(game.id).delete()
      await db.statEntries.where('gameId').equals(game.id).delete()
    }
    await db.games.where('seasonId').equals(seasonId).delete()
    await db.events.where('seasonId').equals(seasonId).delete()
  })
}

/**
 * Removes all players from a season roster without deleting the Player records.
 */
export async function deleteSeasonRoster(seasonId: string): Promise<void> {
  const { error } = await supabase.from('season_rosters').delete().eq('season_id', seasonId)
  if (error) throw new Error(error.message)
  await db.seasonRosters.where('seasonId').equals(seasonId).delete()
}

export async function resetAllData(): Promise<void> {
  // Clears ALL local Dexie tables — cloud data is preserved.
  // User can re-hydrate by logging out and back in.
  await db.transaction('rw', [
    db.teams, db.seasons, db.players, db.seasonRosters,
    db.games, db.lineups, db.lineupSlots, db.statEntries,
    db.events, db.syncMeta, db.opponents, db.drillTemplates,
  ], async () => {
    await Promise.all([
      db.teams.clear(), db.seasons.clear(), db.players.clear(),
      db.seasonRosters.clear(), db.games.clear(), db.lineups.clear(),
      db.lineupSlots.clear(), db.statEntries.clear(),
      db.events.clear(), db.syncMeta.clear(),
      db.opponents.clear(), db.drillTemplates.clear(),
    ])
  })
}

/**
 * Nuclear option — permanently deletes ALL teams and ALL data for the current
 * user from both Supabase and local Dexie. Cannot be undone.
 */
export async function deleteEverythingEverywhere(): Promise<void> {
  const userId = useAuthStore.getState().user?.id
  if (!userId) throw new Error('Not authenticated')

  // Supabase: delete all teams (CASCADE handles all child tables).
  // RLS ensures only the user's own teams are deleted.
  const { error: teamsErr } = await supabase.from('teams').delete().eq('user_id', userId)
  if (teamsErr) throw new Error(`Delete teams: ${teamsErr.message}`)

  // Delete all players owned by this user
  const { error: playersErr } = await supabase.from('players').delete().eq('user_id', userId)
  if (playersErr) throw new Error(`Delete players: ${playersErr.message}`)

  // Wipe all local Dexie tables
  await resetAllData()
}
